import { Button } from "@circleco/compass/components/Button";
import { ButtonSkeleton } from "@circleco/compass/components/ButtonSkeleton";
import { use } from "react";

import { t } from "@/i18n-js/instance";
import { OfferActionsContext } from "@/react/components/Paywalls/CheckoutWithSession/pages/OfferPage/OfferActionsContext";
import { UpgradeFinancialSummary } from "@/react/components/Paywalls/CheckoutWithSession/pages/OfferPage/UpgradeFinancialSummary";
import { classNames } from "@/react/helpers/twMergeWithCN";
import type { UpgradePreview } from "@/react/types/Paywalls/Checkout/UpgradePreview";

interface UpgradeOfferActionsProps {
  hasAcceptanceStarted: boolean;
  isAccepted: boolean;
  isAccepting: boolean;
  isDeclining: boolean;
  isOrderDetailsOpen: boolean;
  isProcessing: boolean;
  isRefreshing: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onOpenOrderDetails: (button: HTMLButtonElement | HTMLAnchorElement) => void;
  preview: UpgradePreview;
}

export const UpgradeOfferActions = ({
  hasAcceptanceStarted,
  isAccepted,
  isAccepting,
  isDeclining,
  isOrderDetailsOpen,
  isProcessing,
  isRefreshing,
  onAccept,
  onDecline,
  onOpenOrderDetails,
  preview,
}: UpgradeOfferActionsProps) => {
  const isSticky = use(OfferActionsContext)?.placement === "sticky";
  const buttons = (
    <>
      {isRefreshing ? (
        <ButtonSkeleton fullWidth />
      ) : (
        <Button
          type="button"
          variant="primary"
          fullWidth
          data-testid="upgrade-accept-button"
          disabled={isAccepted || isAccepting || isDeclining}
          loading={isAccepting}
          onClick={onAccept}
        >
          {isProcessing
            ? t(["paywall_checkout.offer_page.upgrade", "processing"])
            : preview.labels.cta}
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        fullWidth
        disabled={hasAcceptanceStarted || isDeclining}
        loading={isDeclining}
        onClick={onDecline}
      >
        {t(["paywall_checkout.offer_page", "no_thank_you"])}
      </Button>
    </>
  );

  return (
    <div
      className={classNames("w-full max-w-xl", {
        "space-y-2 [@container(min-width:768px)]:space-y-3": isSticky,
        "space-y-3": !isSticky,
      })}
    >
      <UpgradeFinancialSummary
        preview={preview}
        isOrderDetailsOpen={isOrderDetailsOpen}
        isRefreshing={isRefreshing}
        isSticky={isSticky}
        onOpenOrderDetails={onOpenOrderDetails}
      />
      {isSticky ? (
        <div className="flex flex-col gap-2 [@container(min-width:768px)]:gap-3">
          {buttons}
        </div>
      ) : (
        buttons
      )}
    </div>
  );
};
